import React, { useEffect, useState } from "react"
import { useCMSReady } from "../../../../domain/CMS/useCMSReady"
import {
  ReferralPacket,
  ReferralStats,
} from "../../../../domain/Referral/schema"
import { getValueFromCMSMap } from "../../../../domain/CMS/utils"
import Typography, { typographyTypes } from "../Typography"
import {
  buildPresentingProblemFromPacket,
  formatM3AttendeesFromPacket,
  isM3MemberIntakeFromPacket,
} from "../../../referrals/memberIntakeDisplay"
import { MemberIntakePresentingProblemValue } from "../../../referrals/MemberIntakePresentingProblemValue"

interface CaseNumberDetailsProps {
  referralPacket: ReferralPacket
  referralStatsList: ReferralStats[]
  caseSessionLimit: number
}

type CaseDetailRow = {
  key: string
  displayName: string
  value?: string | number
  customValue?: React.ReactNode
}

export const CaseNumberDetails: React.FC<CaseNumberDetailsProps> = ({
  referralPacket,
  referralStatsList,
  caseSessionLimit,
}) => {
  const { cms: cmsContent } = useCMSReady([
    "requestReferralModal",
    "referrerCodeMap",
  ])
  const caseNumberContent = cmsContent?.requestReferralModal
  const referrerCodeMap = cmsContent?.referrerCodeMap
  const isM3Case = isM3MemberIntakeFromPacket(referralPacket.memberIntake)
  const m3PresentingProblem = buildPresentingProblemFromPacket(
    referralPacket.memberIntake,
  )

  const totalSessionsCompleted = referralStatsList.reduce(
    (acc, stats) =>
      stats.referralStatusDisplayId == "closed"
        ? acc + stats.completedSessionCount
        : acc,
    0,
  )
  const totalSessionsScheduled = referralStatsList.reduce(
    (acc, stats) =>
      stats.referralStatusDisplayId == "closed"
        ? acc + stats.scheduledSessionCount
        : acc,
    0,
  )
  const [referralSessionLimit, setReferralSessionLimit] = useState<number>(
    caseSessionLimit - (totalSessionsCompleted + totalSessionsScheduled),
  )

  useEffect(() => {
    const newReferralSessionLimit =
      caseSessionLimit - (totalSessionsCompleted + totalSessionsScheduled)
    setReferralSessionLimit(newReferralSessionLimit)
  }, [caseSessionLimit, totalSessionsCompleted, totalSessionsScheduled])

  const data_part1: CaseDetailRow[] = [
    {
      key: "organization",
      displayName: caseNumberContent?.caseDetailsOrganizationLabel || "",
      value: referralPacket?.organizationName,
    },
    {
      key: "referralType",
      displayName: caseNumberContent?.caseDetailsReferralTypeLabel || "",
      value: getValueFromCMSMap(
        referralPacket?.caseReferredByCode,
        referrerCodeMap,
      ),
    },
  ]

  const data_part2: CaseDetailRow[] = [
    {
      key: "caseSessionLimit",
      displayName: caseNumberContent?.caseDetailsCaseSessionLimitLabel || "",
      value: caseSessionLimit,
    },
    {
      key: "sessionsUsed",
      displayName: caseNumberContent?.caseDetailsSessionsUsedLabel || "",
      value: totalSessionsCompleted + totalSessionsScheduled,
    },
    {
      key: "referralSessionLimit",
      displayName:
        caseNumberContent?.caseDetailsReferralSessionLimitLabel || "",
      value: referralSessionLimit,
    },
  ]

  const data_part3: CaseDetailRow[] = isM3Case
    ? [
        {
          key: "attendees",
          displayName: caseNumberContent?.caseDetailsAttendeesLabel || "",
          value: formatM3AttendeesFromPacket(referralPacket.memberIntake),
        },
        {
          key: "presentingProblem",
          displayName:
            caseNumberContent?.caseDetailsPresentingProblemLabel || "",
          customValue: (
            <MemberIntakePresentingProblemValue
              presentingProblem={m3PresentingProblem}
            />
          ),
        },
      ]
    : [
        {
          key: "attendees",
          displayName: caseNumberContent?.caseDetailsAttendeesLabel || "",
          value: referralPacket?.attendees,
        },
        {
          key: "presentingProblem",
          displayName:
            caseNumberContent?.caseDetailsPresentingProblemLabel || "",
          value: referralPacket?.presentingProblem,
        },
      ]

  const data = [...data_part1, ...data_part2, ...data_part3]
  return (
    <div className="border border-solid border-gray-300 rounded-lg overflow-hidden mt-5 shadow-sm box-border">
      <div className="w-full h-full mt-1 mb-4">
        <div className="w-full">
          <ul className="divide-y bg-white p-0 m-0">
            <li className="flex justify-between py-1 font-bold text-gray-700">
              <div className="w-2/5 pl-6">
                <Typography
                  text={`${caseNumberContent?.caseDetailsCaseLabel}:`}
                  styleOverrides="font-semibold"
                  variant={typographyTypes.NORMAL}
                />
              </div>
              <div className="w-3/5 pr-6">
                <Typography
                  text={referralPacket.caseID}
                  styleOverrides="font-semibold"
                  variant={typographyTypes.NORMAL}
                />
              </div>
            </li>
            {data.map(({ key, displayName, value, customValue }, index) => (
              <li
                key={key}
                className={`flex py-1 ${
                  index % 2 === 0 ? "bg-primary-blue-light" : "bg-white"
                }`}
              >
                <div className="w-2/5 pl-6 font-medium text-gray-700">
                  <Typography
                    text={`${displayName}:`}
                    styleOverrides="font-medium"
                    variant={typographyTypes.NORMAL}
                  />
                </div>
                <div className="w-3/5 pr-6 text-gray-900">
                  {customValue ?? (
                    <Typography text={value} variant={typographyTypes.NORMAL} />
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  )
}
